/*
 * Copyright (c) Nexign, JSC, 2021-2022.
 */
/**
 * Account Info Email Delivery Component
 *
 * @author Aleksandr Perkhunov
 * @author Yakov Gushchin
 * @author Ilya Babanov
 * @version 1.22.0
 * @since 1.11.0
 */

import { useEffect, type FC } from 'react';

import { useFormikContext } from 'formik';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { BadgeStatus } from '@/main/components/common';
import { EBadgeStatus } from '@/main/components/common/Badge/BadgeStatus';
import { ImgStyled } from '@/main/components/common/Block/Img';
import ArrowTooltip from '@/main/components/common/Tooltip/Tooltip';
import { LoaderImageStyled } from '@/main/modules/loader/components/styled/LoaderStyled';
import { FeatureNameEnum } from '@/main/modules/role/constants/guard/guardConstants';
import {
    makeSelectFeatureEnabled,
    makeSelectUserIsCoordinator,
} from '@/main/pages/PageAuth/selectors/authSelectors';
import { IRouteParams } from '@/main/routes/constants/routesConstants';
import { stringFormatParams } from '@/main/utils/stringFormat';
import { icons } from '@assets/index';
import { FormBlockStyled, FormCheckboxDivStyled, FormLabelHintStyled } from '@components/common/FormStyled/FormStyled';
import FormFieldCheckbox from '@modules/form/components/FormFieldCheckbox';
import FormFieldInput from '@modules/form/components/FormFieldInput';
import { makeSelectPageAccountInfoTranslation } from '@modules/internationalization/intSelectors';
import ServiceUserDenied from '@modules/role/components/ServiceUserDenied';
import { CheckboxServiceUserDeniedStyled } from '@modules/role/components/styled/CheckboxServiceUserDeniedStyled';
import { AuthorityRoleEnum } from '@pages/PageAuth/constants/authConstants';
import { useCheckCustomRole } from '@utils/useCheckCustomRole';

import { AccountInfoDeliveryFields } from '../../pageAccountInfoDeliveryConstant';
import { useGetAccountInfoDeliveryMetaState, useLazyGetAccountInfoDeliveryEmailInquiryQuery } from '../../store';
import { ClientId } from '../../types';
import { IDeliveryEmail } from '../../types/pageAccountInfoTypes';
import { AccountDeliveryTypeStyled } from '../styled/AccountInfoDeliveryStyled';



export const AccountDeliveryEmail: FC = () => {
    const isCoordinator = useSelector(makeSelectUserIsCoordinator());
    
    const { delivery: tDelivery } = useSelector(makeSelectPageAccountInfoTranslation());
    
    const isBillingDeliveryEmailInquiryEnabled = 
        useSelector(makeSelectFeatureEnabled(FeatureNameEnum.BILLING_DELIVERY_EMAIL_INQUIRY));
    const isBillingDeliveryEmailInquirySearchEnabled = 
        useSelector(makeSelectFeatureEnabled(FeatureNameEnum.BILLING_DELIVERY_EMAIL_SEARCH_INQUIRIES));
    
    const isCustomRoleCheckAvailable = useCheckCustomRole(false, AuthorityRoleEnum.ROLE_DOCS_MANAGE);
    
    const { clientId } = useParams<IRouteParams>();

    const isCheckboxEnabled = isCoordinator || isCustomRoleCheckAvailable;

    const { getFieldProps, setFieldValue } = useFormikContext();

    const [getAccountInfoDeliveryEmailInquiry, 
        {
            data: delivetyEmailInquiryData, 
            isLoading: isDeliveryEmailDataLoading,
        },
    ] = useLazyGetAccountInfoDeliveryEmailInquiryQuery();

    const {
        data: deliveryMeta,
        isLoading: deliveryMetaIsLoading,
        isSuccess: deliveryMetaIsSuccess,
    } = useGetAccountInfoDeliveryMetaState(ClientId(clientId));

    const { email, inquiryId }  =  delivetyEmailInquiryData ?? {} as IDeliveryEmail;

    const isDeliveryMetaSuccesfullyLoaded = !deliveryMetaIsLoading && deliveryMetaIsSuccess;
    const isEmailDeliveryViaInquiry = 
        isBillingDeliveryEmailInquiryEnabled && 
        isBillingDeliveryEmailInquirySearchEnabled;
    const showDeliveryEmailLoader = isEmailDeliveryViaInquiry && isDeliveryMetaSuccesfullyLoaded;

    const emailChangeHint = isEmailDeliveryViaInquiry ? tDelivery.emailChangeHint : '';

    const tooltipHtmlText = stringFormatParams(tDelivery.tooltipText, {
        'inquiryId': String(inquiryId), 
        'email': email });

    const badgeText =
        deliveryMeta?.deliveryData.useEmail 
            ? tDelivery.inquiryStatuses.changing 
            : tDelivery.inquiryStatuses.connectiong;

    const isEmailInputDisabled = !getFieldProps(AccountInfoDeliveryFields.useEmail).value || !isCheckboxEnabled;

    const useEmailFromMeta = getFieldProps(AccountInfoDeliveryFields.useEmail).value;
    const emailFromMeta = getFieldProps(AccountInfoDeliveryFields.email).value;

    const showInquiryInfoIcon = 
        isBillingDeliveryEmailInquiryEnabled && 
        useEmailFromMeta &&
        !deliveryMetaIsLoading &&
        (
            deliveryMeta?.deliveryData.email !== emailFromMeta ||
            deliveryMeta?.deliveryData.useEmail !== useEmailFromMeta
        );

    useEffect(() => {
        if (showDeliveryEmailLoader) {
            getAccountInfoDeliveryEmailInquiry(ClientId(clientId));
        }
    }, [clientId, getAccountInfoDeliveryEmailInquiry, showDeliveryEmailLoader]);

    useEffect(() => {
        if (
            isBillingDeliveryEmailInquiryEnabled && 
            isEmailInputDisabled && 
            deliveryMeta && 
            !deliveryMetaIsLoading
        ) {
            setFieldValue(AccountInfoDeliveryFields.email, deliveryMeta.deliveryData.email);
        }
    },[deliveryMeta, deliveryMetaIsLoading, isBillingDeliveryEmailInquiryEnabled, isEmailInputDisabled, setFieldValue]);

    return (
        <AccountDeliveryTypeStyled>
            <FormBlockStyled
                className="delivery__address-field-checkbox"
                style={{ width: '100%', justifyContent: 'space-between', height: '34px' }}>
                <ServiceUserDenied renderComponent={disabled => (
                    <>
                        <FormCheckboxDivStyled className="checkbox-field">
                            <CheckboxServiceUserDeniedStyled
                                disabled={!isCheckboxEnabled || disabled}
                            >
                                <FormFieldCheckbox
                                    dataName="delivery-use-email"
                                    disabled={!isCheckboxEnabled || disabled}
                                    label={tDelivery.useEmail}
                                    name={AccountInfoDeliveryFields.useEmail}
                                />
                            </CheckboxServiceUserDeniedStyled>
                            {showInquiryInfoIcon && (
                                <FormLabelHintStyled className="close-icon-label">
                                        <ArrowTooltip
                                            placement='top'
                                            title={emailChangeHint}>
                                                <ImgStyled className="small" src={icons.infoBlueIcon} />
                                        </ArrowTooltip>
                                </FormLabelHintStyled>)}
                        </FormCheckboxDivStyled>
                        {showDeliveryEmailLoader && (isDeliveryEmailDataLoading ? (
                            <LoaderImageStyled>
                                <span />
                                <span />
                                <span />
                                <i />
                            </LoaderImageStyled>
                        ) : (
                            !!inquiryId &&
                            <ArrowTooltip
                                maxWidth={'400px'}
                                message={tooltipHtmlText}
                                placement='top'
                            >
                                <div>
                                    <BadgeStatus
                                        className="email-delivery-inquiry-status"
                                        status={EBadgeStatus.future}
                                        text={badgeText}
                                    />
                                </div>
                            </ArrowTooltip>
                            
                        ))}
                    </>
                )}/>
            </FormBlockStyled>            
            <FormFieldInput
                required
                dataName="delivery-contact-email"
                disabled={isEmailInputDisabled}
                label={tDelivery.email}
                name={AccountInfoDeliveryFields.email}
            />                   
        </AccountDeliveryTypeStyled >
    )
};
